import axios from "axios";
import { validateCommerceSession } from "../../utils/validateCommerceSession";
import { getLanguage } from "./accessToken";

/**
 * Helpers
 */
function decodeJwtPayload(token?: string) {
  try {
    if (!token || typeof token !== "string") return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function pickAccountIdFromOkta(oktaPayload: any): string | undefined {
  const mss: string | undefined = oktaPayload?.mss;
  const aboFromMss = mss?.match(/aboNum=(\d+)/)?.[1];
  return aboFromMss;
}

function cartBaseUrl() {
  const base = process.env.REACT_APP_CART_URL;
  const site = process.env.REACT_APP_BASE_SITE_ID;
  if (!base || !site) {
    throw new Error("Missing REACT_APP_CART_URL or REACT_APP_BASE_SITE_ID");
  }
  return `${base}/${site}`;
}

/**
 * IMPORTANT:
 * Anonymous endpoints must NOT receive Authorization: Bearer <access_token>
 */
function stripBearerForAnonymous(url: string, headers: any) {
  if (!url.includes("/users/anonymous/")) return headers;
  if (headers?.Authorization) delete headers.Authorization;
  if (headers?.authorization) delete headers.authorization;
  return headers;
}

type AuthContext = {
  isAuthenticated: boolean;
  partyId?: string;
  accountId?: string;
  accessToken?: string;
  idTokenHeader?: string;
  tokens: any;
};

function getAuthContext(): AuthContext {
  let tokens: any = {};
  try {
    tokens = JSON.parse(localStorage.getItem("commerce_token_storage") || "{}");
  } catch {
    tokens = {};
  }

  const userInfoRaw = localStorage.getItem("user-info");
  let userInfo: any = {};
  try {
    userInfo = userInfoRaw ? JSON.parse(userInfoRaw) : {};
  } catch {
    userInfo = {};
  }

  const oktaRawStr = localStorage.getItem("okta-token-storage");
  let oktaRaw: any = {};
  try {
    oktaRaw = oktaRawStr && oktaRawStr !== "{}" ? JSON.parse(oktaRawStr) : {};
  } catch {
    oktaRaw = {};
  }

  const oktaIdToken = oktaRaw?.idToken?.value;
  const hasOkta = !!oktaIdToken;

  const oktaPayload = decodeJwtPayload(oktaIdToken);

  const partyIdFromOkta =
    oktaPayload?.partyId ||
    oktaPayload?.gbl_partyid ||
    oktaPayload?.login_partyid;

  const accountIdFromOkta = pickAccountIdFromOkta(oktaPayload);

  // Prefer commerce idToken values, then user-info, then Okta payload
  const partyId =
    tokens?.idToken?.partyId ?? userInfo?.partyId ?? partyIdFromOkta;

  const accountId =
    tokens?.idToken?.accountId ?? userInfo?.accountId ?? accountIdFromOkta;

  const accessToken = tokens?.accessToken?.access_token;
  const idTokenHeader = tokens?.idToken?.id_token;

  const isAuthenticated =
    hasOkta &&
    !!partyId &&
    !!accountId &&
    partyId !== "anonymous" &&
    accountId !== "anonymous";

  return {
    isAuthenticated,
    partyId,
    accountId,
    accessToken,
    idTokenHeader,
    tokens,
  };
}

function buildHeaders(ctx: AuthContext, includeJson = false) {
  const headers: any = { Accept: "application/json" };
  if (includeJson) headers["Content-Type"] = "application/json";

  // keep sending id_token if Lynx expects it
  if (ctx.idTokenHeader) headers.id_token = ctx.idTokenHeader;

  // send bearer for anonymous too (when present)
  if (ctx.accessToken) headers.Authorization = `Bearer ${ctx.accessToken}`;

  return headers;
}

function getCartId(ctx: AuthContext) {
  return ctx.isAuthenticated
    ? localStorage.getItem("cartCode")
    : localStorage.getItem("cartId");
}

async function getOrCreateAnonymousCart(): Promise<string> {
  await validateCommerceSession();

  const existingCartId = localStorage.getItem("cartId");

  if (existingCartId) {
    try {
      const ctx = getAuthContext();
      const url = `${cartBaseUrl()}/users/anonymous/carts/${existingCartId}`;

      let headers = buildHeaders(ctx, true);
      headers = stripBearerForAnonymous(url, headers);

      await axios.get(url, { headers, withCredentials: true });

      return existingCartId;
    } catch {
      localStorage.removeItem("cartId");
      localStorage.removeItem("anonCartGuid");
    }
  }

  const ctx = getAuthContext();
  const url = `${cartBaseUrl()}/users/anonymous/carts`;

  let headers = buildHeaders(ctx, true);
  headers = stripBearerForAnonymous(url, headers);

  const res = await axios.post(url, {}, { headers, withCredentials: true });

  const cartId: string | undefined = res?.data?.code;
  const cartGuid: string | undefined = res?.data?.guid;

  if (!cartId) {
    throw new Error(
      "[Cart] Failed to create anonymous cart: missing code in response",
    );
  }

  localStorage.setItem("cartId", cartId);

  if (cartGuid) {
    localStorage.setItem("anonCartGuid", cartGuid);
  }

  return cartId;
}

async function ensureLoggedInCart(ctx: AuthContext): Promise<string> {
  if (!ctx.isAuthenticated) {
    throw new Error(
      "[Cart] ensureLoggedInCart called for unauthenticated user",
    );
  }

  const anonCartGuid = localStorage.getItem("anonCartGuid");

  // if tokens.cart_id is actually the anonymous cart UUID, ignore it once logged in
  if (
    ctx.tokens?.cart_id &&
    anonCartGuid &&
    ctx.tokens.cart_id === anonCartGuid
  ) {
    delete ctx.tokens.cart_id;
    localStorage.setItem("commerce_token_storage", JSON.stringify(ctx.tokens));
  }

  const cachedCartCode = localStorage.getItem("cartCode");

  if (cachedCartCode) {
    try {
      const res = await axios.get(
        `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts/${cachedCartCode}`,
        { headers: buildHeaders(ctx, false), withCredentials: true },
      );

      const validCartCode: string | undefined = res?.data?.code;

      if (validCartCode) {
        localStorage.setItem("cartCode", validCartCode);
        ctx.tokens.cart_id = validCartCode;
        localStorage.setItem(
          "commerce_token_storage",
          JSON.stringify(ctx.tokens),
        );
        return validCartCode;
      }
    } catch {
      localStorage.removeItem("cartCode");

      if (ctx.tokens?.cart_id) {
        delete ctx.tokens.cart_id;
      }

      localStorage.setItem(
        "commerce_token_storage",
        JSON.stringify(ctx.tokens),
      );
    }
  }

  const headers = buildHeaders(ctx, false);

  try {
    const res = await axios.get(
      `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts/default`,
      { headers, withCredentials: true },
    );

    const cartId: string | undefined = res?.data?.code;
    if (!cartId) {
      throw new Error("[Cart] Default cart response missing code");
    }

    localStorage.setItem("cartCode", cartId);
    ctx.tokens.cart_id = cartId;
    localStorage.setItem("commerce_token_storage", JSON.stringify(ctx.tokens));
    return cartId;
  } catch (err: any) {
    const status = err?.response?.status;
    const type = err?.response?.data?.errors?.[0]?.type;

    // if default cart doesn't exist yet, create it
    if (status === 404 || type === "LynxOC_CartNotFoundError") {
      const createHeaders = buildHeaders(ctx, true);

      const createRes = await axios.post(
        `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts`,
        {},
        { headers: createHeaders, withCredentials: true },
      );

      const newCartId: string | undefined = createRes?.data?.code;
      if (!newCartId) {
        throw new Error("[Cart] Create logged-in cart response missing code");
      }

      localStorage.setItem("cartCode", newCartId);
      ctx.tokens.cart_id = newCartId;
      localStorage.setItem(
        "commerce_token_storage",
        JSON.stringify(ctx.tokens),
      );

      return newCartId;
    }

    throw err;
  }
}

/**
 * API call to get/create cart-id
 * - If logged in: ensures default cart exists, caches cartCode
 * - If anonymous: ensures anonymous cart exists, caches cartId
 */
export async function getOrCreateCart() {
  await validateCommerceSession();

  const ctx = getAuthContext();

  if (!ctx.isAuthenticated) {
    const anonCartId = await getOrCreateAnonymousCart();
    return anonCartId ? { code: anonCartId } : null;
  }

  const cartId = await ensureLoggedInCart(ctx);
  return cartId ? { code: cartId } : null;
}

/**
 * API call to merge anonymous cart entries into logged-in cart
 */
export async function cartMergingCall() {
  await validateCommerceSession();

  const anonymousCartGuid = localStorage.getItem("anonCartGuid");
  if (!anonymousCartGuid) return;

  const ctx = getAuthContext();
  if (!ctx.isAuthenticated) return;

  const targetCartCode = await ensureLoggedInCart(ctx);
  if (!targetCartCode) return;

  const headers = buildHeaders(ctx, false);

  try {
    const response = await axios({
      method: "post",
      url:
        `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts/${targetCartCode}` +
        `?anonymousCartUuid=${encodeURIComponent(anonymousCartGuid)}&includeEntries=true`,
      headers,
      withCredentials: true,
    });

    localStorage.removeItem("cartId");
    localStorage.removeItem("anonCartGuid");

    // persist logged-in cart code
    localStorage.setItem("cartCode", targetCartCode);
    ctx.tokens.cart_id = targetCartCode;
    localStorage.setItem("commerce_token_storage", JSON.stringify(ctx.tokens));

    await getCartEntries();
    return response.data;
  } catch (err: any) {
    const status = err?.response?.status;
    const msg = err?.response?.data?.message;

    if (
      status === 404 ||
      status === 400 ||
      msg?.includes("Anonymous cart not found")
    ) {
      localStorage.removeItem("cartId");
      localStorage.removeItem("anonCartGuid");
      await ensureLoggedInCart(getAuthContext());
      return;
    }

    console.error("[Cart] cart merge failed", err);
    throw err;
  }
}

/**
 * API call to fetch cart entries
 */
export async function getCartEntries() {
  await validateCommerceSession();

  const buildEntriesUrl = (id: string, auth: AuthContext) =>
    auth.isAuthenticated
      ? `${cartBaseUrl()}/users/${auth.partyId}/account/${auth.accountId}/carts/${id}/entries?lang=${getLanguage()}&pageSize=100`
      : `${cartBaseUrl()}/users/anonymous/carts/${id}/entries?lang=${getLanguage()}&pageSize=100`;

  const clearCartCache = (auth: AuthContext) => {
    localStorage.removeItem("totalCartProductsCount");
    if (auth.isAuthenticated) {
      localStorage.removeItem("cartCode");
    } else {
      localStorage.removeItem("cartId");
    }
  };

  // guarantee a non-null cartId (or throw)
  const resolveCartId = async (auth: AuthContext): Promise<string> => {
    const existing = getCartId(auth);
    if (existing) return existing;

    const created = auth.isAuthenticated
      ? await ensureLoggedInCart(auth)
      : await getOrCreateAnonymousCart();

    if (!created) throw new Error("Missing cartId");
    return created;
  };

  const request = async (auth: AuthContext, cartId: string) => {
    const url = buildEntriesUrl(cartId, auth);
    let headers = buildHeaders(auth, false);
    headers = stripBearerForAnonymous(url, headers);

    return axios.get(url, { headers, withCredentials: true });
  };

  const ctx = getAuthContext();

  // first attempt (normal path)
  let cartId = await resolveCartId(ctx);

  try {
    const res = await request(ctx, cartId);

    const entries = res?.data?.entries ?? [];
    const totalQty = entries.reduce(
      (sum: number, e: any) => sum + (Number(e?.quantity) || 0),
      0,
    );
    localStorage.setItem("totalCartProductsCount", String(totalQty));

    return res.data;
  } catch (err: any) {
    const status = err?.response?.status;

    // only attempt one recovery retry for these statuses
    if (!(status === 401 || status === 404)) {
      throw err;
    }

    // recovery: clear cache, re-resolve cart, retry once
    clearCartCache(ctx);

    const freshCtx = getAuthContext();
    cartId = await resolveCartId(freshCtx);

    const retryRes = await request(freshCtx, cartId);

    const entries = retryRes?.data?.entries ?? [];
    const totalQty = entries.reduce(
      (sum: number, e: any) => sum + (Number(e?.quantity) || 0),
      0,
    );
    localStorage.setItem("totalCartProductsCount", String(totalQty));

    return retryRes.data;
  }
}

/**
 * API call to add product to cart (anonymous + logged-in)
 */
export async function addCartEntries(
  productCode: string | number,
  quantity: number,
) {
  await validateCommerceSession();

  const ctx = getAuthContext();

  let cartId = getCartId(ctx);
  if (ctx.isAuthenticated) {
    cartId = cartId ?? (await ensureLoggedInCart(ctx));
  } else {
    cartId = await getOrCreateAnonymousCart();
  }

  if (!cartId) throw new Error("Missing cartId");

  const url = ctx.isAuthenticated
    ? `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts/${cartId}/entries?apiClient=WEB&lang=${getLanguage()}`
    : `${cartBaseUrl()}/users/anonymous/carts/${cartId}/entries?apiClient=WEB&lang=${getLanguage()}`;

  let headers = buildHeaders(ctx, true);
  headers = stripBearerForAnonymous(url, headers);

  const data = {
    product: { code: String(productCode) },
    quantity,
  };

  try {
    const res = await axios.post(url, data, { headers, withCredentials: true });
    getCartEntries().catch(() => {});
    return res.data;
  } catch (err: any) {
    const status = err?.response?.status;

    // Anonymous recovery: cartId stale/invalid -> recreate and retry once
    if (!ctx.isAuthenticated && (status === 401 || status === 404)) {
      localStorage.removeItem("cartId");

      const freshCartId = await getOrCreateAnonymousCart();
      if (!freshCartId) throw err;

      const retryUrl = `${cartBaseUrl()}/users/anonymous/carts/${freshCartId}/entries?apiClient=WEB&lang=${getLanguage()}`;

      let retryHeaders = buildHeaders(getAuthContext(), true);
      retryHeaders = stripBearerForAnonymous(retryUrl, retryHeaders);

      const retryRes = await axios.post(retryUrl, data, {
        headers: retryHeaders,
        withCredentials: true,
      });

      getCartEntries().catch(() => {});
      return retryRes.data;
    }

    throw err;
  }
}

/**
 * API call to delete cart entries (anonymous + logged-in)
 */
export async function deleteCartEntries(entryNumber: number) {
  await validateCommerceSession();

  const ctx = getAuthContext();

  let cartId = getCartId(ctx);
  if (ctx.isAuthenticated) {
    cartId = cartId ?? (await ensureLoggedInCart(ctx));
  } else {
    cartId = cartId ?? (await getOrCreateAnonymousCart());
  }

  if (!cartId) throw new Error("Missing cartId");

  const url = ctx.isAuthenticated
    ? `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts/${cartId}/entries/${entryNumber}?lang=${getLanguage()}`
    : `${cartBaseUrl()}/users/anonymous/carts/${cartId}/entries/${entryNumber}?lang=${getLanguage()}`;

  let headers = buildHeaders(ctx, false);
  headers = stripBearerForAnonymous(url, headers);

  return axios
    .delete(url, { headers, withCredentials: true })
    .then((res) => res.data);
}

/**
 * API call to update cart entry quantity (deduct/increment) (anonymous + logged-in)
 */
export async function deductCartEntriesCount(
  productCode: string | number,
  quantity: number,
  entryNumber: number,
) {
  await validateCommerceSession();

  const ctx = getAuthContext();

  let cartId = getCartId(ctx);
  if (ctx.isAuthenticated) {
    cartId = cartId ?? (await ensureLoggedInCart(ctx));
  } else {
    cartId = cartId ?? (await getOrCreateAnonymousCart());
  }

  if (!cartId) throw new Error("Missing cartId");

  const url = ctx.isAuthenticated
    ? `${cartBaseUrl()}/users/${ctx.partyId}/account/${ctx.accountId}/carts/${cartId}/entries/${entryNumber}?lang=${getLanguage()}`
    : `${cartBaseUrl()}/users/anonymous/carts/${cartId}/entries/${entryNumber}?lang=${getLanguage()}`;

  let headers = buildHeaders(ctx, true);
  headers = stripBearerForAnonymous(url, headers);

  const data = {
    product: { code: String(productCode) },
    quantity,
  };

  return axios
    .put(url, data, { headers, withCredentials: true })
    .then((res) => res.data);
}
